import { reactive } from 'vue';
import { CLEAR_ERRORMSG, LOGOUT } from '@/store/action_types';
import { SET_ACCOUNT, SET_SESSION_TOKEN } from '@/store/mutation_types';
import { DASHBOARD_ROUTE, LOGIN_ROUTE, NOT_FOUND_404_ROUTE, SUPPORT_SESSION_ROUTE } from '@/router/routes';

/**
 * Support sessions: Screenkode staff using this app as an organization's
 * admin, from the Screenkode admin (admin.screenkode.com).
 *
 * The admin opens this app in a frame named SUPPORT_SESSION_FRAME_NAME, at
 * SUPPORT_SESSION_PATH. The app then asks the admin for its session (READY)
 * until the admin hands it over (START): a token the API holds to that session,
 * and the account to sign in as. The API refuses the token once the session
 * ends — from the admin, by signing out here, or when it runs out — and the
 * admin is told (ENDED).
 *
 * While a session runs here, the app keeps its signed-in state in this frame's
 * sessionStorage instead of the browser's localStorage, so an ordinary sign-in
 * to this app elsewhere in the browser is left exactly as it was.
 *
 * The admin's side is its own src/utils/support_session.ts; the names and
 * messages here must match it. This file is the same in Screenkode and Chabad
 * Digital.
 */

/** The page the admin loads in its frame: a new session starts there. */
export const SUPPORT_SESSION_PATH = '/support-session';

/** The name the admin gives its frame: this app is in a session's frame on any page. */
export const SUPPORT_SESSION_FRAME_NAME = 'screenkode-support-session';

/** Every message between the admin and this app carries this, so unrelated messages are ignored. */
export const SUPPORT_SESSION_CHANNEL = 'screenkode.support-session';

export const SUPPORT_SESSION_MESSAGE = {
  /** app → admin: loaded, and needs its session. Sent until the admin answers. */
  READY: 'ready',
  /** admin → app: the session's token and account — sign in. */
  START: 'start',
  /** app → admin: signed in (the page it opened), or carrying on after a reload. */
  STARTED: 'started',
  /** app → admin: moved to another page (its path). */
  NAVIGATED: 'navigated',
  /** admin → app: the session is over — sign out. */
  END: 'end',
  /** app → admin: the session is over (signed out here, refused by the API…). */
  ENDED: 'ended',
  /** app → admin: what it was sent couldn't be used. */
  FAILED: 'failed',
} as const;

// How often the app asks the admin for its session, and for how long.
const ASK_EVERY_MS = 1000;
const ASK_FOR_MS = 2 * 60 * 1000;

// In this frame's sessionStorage while a session runs here.
const STATE_KEY = 'screenkode.support-session';
const ADMIN_ORIGIN_KEY = 'screenkode.support-session.admin';
const PROBE_KEY = 'screenkode.support-session.probe';

// Where the admin is served from: only a page there may start a session here.
// Local development runs the admin on localhost, allowed in development builds.
const ADMIN_ORIGIN_PATTERN = /^https:\/\/([a-z0-9-]+\.)*(screenkode|chabaddigital)\.com$/i;
const LOCAL_ORIGIN_PATTERN = /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/i;

export const isAllowedAdminOrigin = (origin: string): boolean =>
  ADMIN_ORIGIN_PATTERN.test(origin) || (process.env.NODE_ENV !== 'production' && LOCAL_ORIGIN_PATTERN.test(origin));

export const isFramed = (): boolean => {
  try {
    return window.self !== window.top;
  } catch (error) {
    return true;
  }
};

type SessionState = 'active' | 'ended';

// Where the page was when it loaded, before the router moved anywhere.
const BOOT_LOCATION: string = (() => {
  try {
    return `${window.location.pathname}${window.location.search}${window.location.hash}` || '/';
  } catch (error) {
    return '/';
  }
})();

const pathOnly = (location: string): string => (location.split(/[?#]/)[0] || '/').replace(/\/+$/, '') || '/';

/** Loaded at the session page, in a frame: a new session starts here. */
const BOOTED_AT_ENTRY: boolean = isFramed() && pathOnly(BOOT_LOCATION) === SUPPORT_SESSION_PATH;

const isNamedFrame = (): boolean => {
  try {
    return window.name === SUPPORT_SESSION_FRAME_NAME;
  } catch (error) {
    return false;
  }
};

// sessionStorage, when it can be used (a private window or blocked storage
// throws on the first touch).
const sessionStore = (): Storage | null => {
  try {
    const storage = window.sessionStorage;
    storage.getItem(STATE_KEY);
    return storage;
  } catch (error) {
    return null;
  }
};

const readStored = (key: string): string | null => {
  const storage = sessionStore();
  try {
    return storage ? storage.getItem(key) : null;
  } catch (error) {
    return null;
  }
};

const writeStored = (key: string, value: string): void => {
  const storage = sessionStore();
  try {
    if (storage) storage.setItem(key, value);
  } catch (error) {
    // This page keeps its own copy below.
  }
};

// This page's own copy, for when storage can't be used at all.
let memoryState: SessionState | null = null;
let memoryAdminOrigin: string | null = null;
// Whether the admin already knows the session ended (or ended it itself).
let endTold = false;

const sessionState = (): SessionState | null => {
  if (memoryState) return memoryState;
  const stored = readStored(STATE_KEY);
  return stored === 'active' || stored === 'ended' ? stored : null;
};

/** Whether a session is running in this frame. */
export const isSupportSession = (): boolean => isFramed() && sessionState() === 'active';

/** Whether this is the admin's frame for a session — running, or about to be. */
export const isSupportSessionContext = (): boolean =>
  isFramed() && (isNamedFrame() || BOOTED_AT_ENTRY || sessionState() !== null);

// A stand-in when no storage can be written at all: the state lives for the
// page alone, which is all a session in a frame needs.
const memoryStorage = (): Storage => {
  const data = new Map<string, string>();
  return {
    get length() {
      return data.size;
    },
    clear: () => data.clear(),
    getItem: (key: string) => (data.has(key) ? (data.get(key) as string) : null),
    key: (index: number) => Array.from(data.keys())[index] ?? null,
    removeItem: (key: string) => {
      data.delete(key);
    },
    setItem: (key: string, value: string) => {
      data.set(key, String(value));
    },
  } as Storage;
};

// Chabad Digital is another site than the admin, so a browser that blocks
// third-party site data refuses this frame its storage, and every use of it
// throws. In a session's frame, stand-ins that last as long as the page take
// their place, so the app's own pages keep working. Nothing changes where
// storage can be used.
const standInForBlockedStorage = (): void => {
  if (!isSupportSessionContext()) return;
  (['localStorage', 'sessionStorage'] as const).forEach((name) => {
    try {
      window[name].getItem(PROBE_KEY);
    } catch (error) {
      try {
        Object.defineProperty(window, name, { value: memoryStorage(), configurable: true });
      } catch (defineError) {
        // Left as it is: the session itself keeps its state in memory anyway.
      }
    }
  });
};

standInForBlockedStorage();

let chosenStorage: Storage | null = null;

const chooseStorage = (key: string): Storage => {
  if (!isSupportSessionContext()) return window.localStorage;
  const storage = sessionStore();
  if (storage) {
    try {
      if (BOOTED_AT_ENTRY) {
        // A new session starts here: nothing an earlier one left in this tab
        // is kept — not its token, and not its account.
        storage.removeItem(key);
        storage.removeItem(STATE_KEY);
        storage.removeItem(ADMIN_ORIGIN_KEY);
      }
      storage.setItem(PROBE_KEY, '1');
      storage.removeItem(PROBE_KEY);
      return storage;
    } catch (error) {
      // Not writable: this page's memory instead.
    }
  }
  return memoryStorage();
};

/**
 * Where the store keeps the persisted account (under `key`): localStorage, as
 * always, or this frame's own storage during a support session. Decided once,
 * when the store is made, and kept — signing out of a session clears the
 * session's storage, never the browser's.
 */
export const persistedStateStorage = (key: string): Storage => {
  if (!chosenStorage) chosenStorage = chooseStorage(key);
  return chosenStorage;
};

export const clearPersistedState = (key: string): void => {
  try {
    persistedStateStorage(key).removeItem(key);
  } catch (error) {
    // Nothing to clear.
  }
};

/** Where the admin is, once it has started the session here. */
export const adminOrigin = (): string | null => memoryAdminOrigin || readStored(ADMIN_ORIGIN_KEY);

const beginSupportSession = (origin: string): void => {
  memoryState = 'active';
  memoryAdminOrigin = origin;
  endTold = false;
  writeStored(STATE_KEY, 'active');
  writeStored(ADMIN_ORIGIN_KEY, origin);
};

const markEnded = (): void => {
  memoryState = 'ended';
  writeStored(STATE_KEY, 'ended');
};

/** Tells the admin something, when there is one to tell. */
export const notifyAdmin = (
  type: string,
  payload: Record<string, unknown> = {},
  targetOrigin: string | null = adminOrigin()
): void => {
  if (!isFramed() || !targetOrigin) return;
  try {
    window.parent.postMessage({ channel: SUPPORT_SESSION_CHANNEL, type, ...payload }, targetOrigin);
  } catch (error) {
    // The admin is gone; nothing to tell.
  }
};

/**
 * Ends the session here — signed out, or refused by the API — and tells the
 * admin why, once. Does nothing outside a session.
 */
export const endSupportSession = (reason: string, message?: string | null): void => {
  if (!isSupportSession()) return;
  const target = adminOrigin();
  markEnded();
  if (endTold) return;
  endTold = true;
  notifyAdmin(SUPPORT_SESSION_MESSAGE.ENDED, { reason, message: message || null }, target);
};

/** What the session page shows when what the admin sent couldn't be used. */
export const supportSessionState = reactive({ error: '' });

const queryParam = (location: string, name: string): string | null => {
  try {
    return new URL(location, window.location.origin).searchParams.get(name);
  } catch (error) {
    return null;
  }
};

// A page of this app the session may open, or null.
const appPage = (router: any, candidate: string | null): string | null => {
  if (!candidate || !candidate.startsWith('/') || candidate.startsWith('//') || candidate.startsWith('/\\')) return null;
  try {
    const route = router.resolve(candidate);
    if (!route || !route.matched || !route.matched.length) return null;
    if ([LOGIN_ROUTE, SUPPORT_SESSION_ROUTE, NOT_FOUND_404_ROUTE].includes(route.name)) return null;
    return route.fullPath;
  } catch (error) {
    return null;
  }
};

// Where to go once signed in: the page the admin asked for (the one a reload
// of the admin left open), the page this frame was on when it reloaded
// itself, or the dashboard.
const pageAfterStart = (router: any): string =>
  (BOOTED_AT_ENTRY ? appPage(router, queryParam(BOOT_LOCATION, 'next')) : appPage(router, BOOT_LOCATION)) ||
  router.resolve({ name: DASHBOARD_ROUTE }).fullPath;

/**
 * The app's side of a session: asks the admin for its session and signs in
 * with it, signs out when the admin ends it, and reports each page it moves
 * to. Installed once the store and router exist (main.ts); it does nothing
 * outside the admin's frame.
 */
export const installSupportSessionBridge = (store: any, router: any): void => {
  if (!isSupportSessionContext()) return;

  let accepted = false;
  let asking: ReturnType<typeof setInterval> | null = null;

  const stopAsking = () => {
    if (asking) {
      clearInterval(asking);
      asking = null;
    }
  };

  // READY carries nothing about the session, so it may go to whoever framed
  // the page: only the admin answers it, and only the admin is listened to.
  const askForSession = () => {
    const until = Date.now() + ASK_FOR_MS;
    const ask = () => {
      if (accepted || Date.now() > until) {
        stopAsking();
        return;
      }
      try {
        window.parent.postMessage({ channel: SUPPORT_SESSION_CHANNEL, type: SUPPORT_SESSION_MESSAGE.READY }, '*');
      } catch (error) {
        stopAsking();
      }
    };
    ask();
    asking = setInterval(ask, ASK_EVERY_MS);
  };

  const start = (event: MessageEvent, data: any) => {
    const token = data.sessionToken;
    const account = data.account;
    if (typeof token !== 'string' || !token || !account || typeof account !== 'object' || !account.id) {
      stopAsking();
      supportSessionState.error = 'The admin sent an incomplete session. Go back to it and launch the session again.';
      notifyAdmin(SUPPORT_SESSION_MESSAGE.FAILED, { message: supportSessionState.error }, event.origin);
      return;
    }
    accepted = true;
    stopAsking();
    supportSessionState.error = '';
    beginSupportSession(event.origin);
    store.commit(SET_SESSION_TOKEN, token);
    store.commit(SET_ACCOUNT, { data: account, isLoading: false });
    store.dispatch(CLEAR_ERRORMSG);
    const page = pageAfterStart(router);
    notifyAdmin(SUPPORT_SESSION_MESSAGE.STARTED, { path: page }, event.origin);
    router.replace(page).catch(() => undefined);
  };

  window.addEventListener('message', (event: MessageEvent) => {
    if (event.source !== window.parent || !isAllowedAdminOrigin(event.origin)) return;
    const data = event.data;
    if (!data || typeof data !== 'object' || data.channel !== SUPPORT_SESSION_CHANNEL) return;

    if (data.type === SUPPORT_SESSION_MESSAGE.START) {
      // One session per page: a new session comes with a new frame.
      if (!accepted) start(event, data);
      return;
    }

    if (data.type === SUPPORT_SESSION_MESSAGE.END) {
      const known = adminOrigin();
      if ((known && event.origin !== known) || !isSupportSession()) return;
      // The admin ended it: nothing to tell it back.
      markEnded();
      endTold = true;
      store.dispatch(LOGOUT);
      router.replace({ name: LOGIN_ROUTE }).catch(() => undefined);
    }
  });

  // Pages it settles on — not the session page, and not a move that was
  // cancelled or redirected on the way.
  router.afterEach((to: { fullPath: string; name?: unknown }, from: unknown, failure?: unknown) => {
    if (failure || to.name === SUPPORT_SESSION_ROUTE || !isSupportSession()) return;
    notifyAdmin(SUPPORT_SESSION_MESSAGE.NAVIGATED, { path: to.fullPath });
  });

  const account = store.getters.account;
  const signedIn = Boolean(store.getters.sessionToken && account && account.data);
  if (isSupportSession() && signedIn) {
    // Reloaded itself mid-session with the session intact: carries on.
    accepted = true;
    notifyAdmin(SUPPORT_SESSION_MESSAGE.STARTED, { path: BOOT_LOCATION, resumed: true });
  } else if (sessionState() !== 'ended') {
    // Needs its session — a new one, or again after reloading without it.
    askForSession();
  }
};
